import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Page from "../components/Page";
import {
  IconLayoutDashboard,
  IconListDetails,
  IconCategory,
  IconShoppingCartPlus,
  IconUserCheck,
  IconArrowsExchange,
  IconAlertTriangle,
  IconTool,
  IconCalendarEvent,
  IconScan,
  IconCalculator,
  IconTrash,
  IconFileText,
  IconReportAnalytics,
  IconSettings
} from "@tabler/icons-react";

// Tab Subviews
import AssetDashboardTab from "./AssetViews/AssetDashboardTab";
import AssetRegisterTab from "./AssetViews/AssetRegisterTab";
import AssetCategoriesTab from "./AssetViews/AssetCategoriesTab";
import AssetAcquisitionsTab from "./AssetViews/AssetAcquisitionsTab";
import AssetAssignmentsTab from "./AssetViews/AssetAssignmentsTab";
import AssetTransfersTab from "./AssetViews/AssetTransfersTab";
import AssetDamageTab from "./AssetViews/AssetDamageTab";
import AssetMaintenanceTab from "./AssetViews/AssetMaintenanceTab";
import AssetPreventiveTab from "./AssetViews/AssetPreventiveTab";
import AssetVerificationTab from "./AssetViews/AssetVerificationTab";
import AssetDepreciationTab from "./AssetViews/AssetDepreciationTab";
import AssetDisposalTab from "./AssetViews/AssetDisposalTab";
import AssetDocumentsTab from "./AssetViews/AssetDocumentsTab";
import AssetReportsTab from "./AssetViews/AssetReportsTab";
import AssetSettingsTab from "./AssetViews/AssetSettingsTab";

export default function AssetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "dashboard";

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: IconLayoutDashboard },
    { id: "register", label: "Asset Register", icon: IconListDetails },
    { id: "categories", label: "Categories", icon: IconCategory },
    { id: "acquisitions", label: "Acquisitions", icon: IconShoppingCartPlus },
    { id: "assignments", label: "Assignments", icon: IconUserCheck },
    { id: "transfers", label: "Transfers", icon: IconArrowsExchange },
    { id: "damage", label: "Damage Reports", icon: IconAlertTriangle },
    { id: "maintenance", label: "Maintenance", icon: IconTool },
    { id: "preventive", label: "Preventive", icon: IconCalendarEvent },
    { id: "verification", label: "Verification", icon: IconScan },
    { id: "depreciation", label: "Depreciation", icon: IconCalculator },
    { id: "disposal", label: "Disposal", icon: IconTrash },
    { id: "documents", label: "Documents", icon: IconFileText },
    { id: "reports", label: "Reports", icon: IconReportAnalytics },
    { id: "settings", label: "Settings", icon: IconSettings }
  ];

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  return (
    <Page>
      <div className="space-y-6">
        {/* Top Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
              <span className="p-2 rounded-xl bg-restro-green/10 text-restro-green">
                <IconLayoutDashboard className="w-6 h-6" />
              </span>
              Asset Lifecycle Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Hotel & enterprise fixed asset register, room tracking, QR audits, preventive service, and accounting depreciation.
            </p>
          </div>
        </div>

        {/* 15-Submodule Modern Navigation Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-2 shadow-sm">
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1 px-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = currentTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? "bg-restro-green text-white shadow-md shadow-emerald-500/20"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/60"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? "text-white" : "text-gray-400"}`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Viewport */}
        <div className="transition-all duration-200">
          {currentTab === "dashboard" && <AssetDashboardTab onNavigate={handleTabChange} />}
          {currentTab === "register" && <AssetRegisterTab />}
          {currentTab === "categories" && <AssetCategoriesTab />}
          {currentTab === "acquisitions" && <AssetAcquisitionsTab />}
          {currentTab === "assignments" && <AssetAssignmentsTab />}
          {currentTab === "transfers" && <AssetTransfersTab />}
          {currentTab === "damage" && <AssetDamageTab />}
          {currentTab === "maintenance" && <AssetMaintenanceTab />}
          {currentTab === "preventive" && <AssetPreventiveTab />}
          {currentTab === "verification" && <AssetVerificationTab />}
          {currentTab === "depreciation" && <AssetDepreciationTab />}
          {currentTab === "disposal" && <AssetDisposalTab />}
          {currentTab === "documents" && <AssetDocumentsTab />}
          {currentTab === "reports" && <AssetReportsTab />}
          {currentTab === "settings" && <AssetSettingsTab />}
        </div>
      </div>
    </Page>
  );
}
